import {zodResolver} from '@hookform/resolvers/zod';
import {TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import React from 'react';
import type { UseFormReturn} from 'react-hook-form';
import {Controller, useForm} from 'react-hook-form';
import {DatePicker} from 'src/design/atoms/DatePicker';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {z} from 'zod';

const requiredMessage = 'This field is required.';

const jobHazardAnalysisFormSchema = z.object({
  subjectName: z.string().min(1, requiredMessage),
  location: z.string(),
  workStartDate: z
    .date()
    .nullable()
    .refine(value => value !== null, {message: requiredMessage}),
  workFinishDate: z
    .date()
    .nullable()
    .refine(value => value !== null, {message: requiredMessage}),
});

/** Validated, submit-time shape (dates guaranteed non-null). */
export type JobHazardAnalysisFormValues = z.infer<
  typeof jobHazardAnalysisFormSchema
>;

/** Pre-validation field shape used for defaultValues/reset/live editing. */
export type JobHazardAnalysisFormFields = z.input<
  typeof jobHazardAnalysisFormSchema
>;

const jobHazardAnalysisFormDefaultValues: JobHazardAnalysisFormFields = {
  subjectName: '',
  location: '',
  workStartDate: null,
  workFinishDate: null,
};

export type JobHazardAnalysisFormApi = UseFormReturn<
  JobHazardAnalysisFormFields,
  unknown,
  JobHazardAnalysisFormValues
>;

export function useJobHazardAnalysisForm(): JobHazardAnalysisFormApi {
  return useForm<
    JobHazardAnalysisFormFields,
    unknown,
    JobHazardAnalysisFormValues
  >({
    defaultValues: jobHazardAnalysisFormDefaultValues,
    resolver: zodResolver(jobHazardAnalysisFormSchema),
  });
}

export interface JobHazardAnalysisFormProps {
  form: JobHazardAnalysisFormApi;
  mode: 'edit' | 'create' | 'view';
  disabled?: boolean;
}

export const JobHazardAnalysisForm: React.FC<JobHazardAnalysisFormProps> = ({
  form,
  mode,
  disabled,
}) => {
  const {control} = form;

  return (
    <Stack gap="md">
      <SectionCard title="General">
        <Box p="md">
          <Controller
            name="subjectName"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                required
                label="Subject"
                placeholder="e.g., Roof Repair - Building A"
                error={fieldState.error?.message}
                disabled={disabled}
                mb="md"
                {...field}
              />
            )}
          />
          <Controller
            name="location"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Location(s)"
                placeholder="e.g., Building A, Floor 3"
                error={fieldState.error?.message}
                disabled={disabled}
                {...field}
              />
            )}
          />
        </Box>
      </SectionCard>

      <SectionCard title="Schedule">
        <Box p="md">
          <SimpleGrid cols={{base: 1, lg: 2}} verticalSpacing="0">
            <Controller
              name="workStartDate"
              control={control}
              render={({field, fieldState}) => (
                <DatePicker
                  label="Work Start"
                  value={field.value}
                  onDateChangeCallback={field.onChange}
                  readonly={mode === 'view'}
                  error={!!fieldState.error}
                  disabled={disabled}
                  required
                />
              )}
            />
            <Controller
              name="workFinishDate"
              control={control}
              render={({field, fieldState}) => (
                <DatePicker
                  label="Work Finish"
                  value={field.value}
                  onDateChangeCallback={field.onChange}
                  readonly={mode === 'view'}
                  error={!!fieldState.error}
                  disabled={disabled}
                  required
                />
              )}
            />
          </SimpleGrid>
        </Box>
      </SectionCard>
    </Stack>
  );
};
